import React from "react";
import {cn} from "@/lib/utils";
import ProfileImage from "@/main/react/buttons/ProfileImage";
import type {Localizer} from "@/main/react/Types";

/**
 * The attribution row is aligned to its FIRST LINE, not to the centre of the text column, and the
 * text column is a plain inline flow rather than a flex box. Both halves are load-bearing once the
 * subtitle wraps — which happens on a phone on any card carrying "started · updated · no replies":
 *
 *  - `items-center` centred the avatar against the whole (possibly three-line) column, leaving it
 *    beside the timestamps instead of beside the username;
 *  - an `inline-flex flex-wrap` column wraps between ITEMS, and `details` is one item, so the whole
 *    subtitle dropped to the next line and the first line held a lone `username ·`.
 *
 * At one line with the `size-5` avatar every feed card uses, this renders identically to the old row.
 * These two strings are hand-mirrored in
 * MainService/src/main/jte/main_pages/components/ssr_home_first_card.jte for the server-painted first
 * home card; feedCardBoxParity.spec.ts pins them together. Rationale:
 * designs/01-now/2026-08-03-author-attribution-first-line-alignment-design.md.
 */
export const AUTHOR_ATTRIBUTION_ROW_CLASS = "flex min-w-0 items-start gap-2 text-sm leading-5";
export const AUTHOR_ATTRIBUTION_TEXT_CLASS = "min-w-0 flex-1";

function getAvatarFallback(label?: string | null): string {
    const trimmedLabel = label?.trim();

    if (!trimmedLabel) {
        return "?";
    }

    return trimmedLabel.charAt(0).toUpperCase();
}

export function AuthorAttribution(props: {
    name: string;
    /** For the avatar's localized `alt` (round 25 `D8`); `ProfileImage` requires one. */
    localizer: Localizer;
    href?: string | null;
    imageUrl?: string | null;
    avatarSeed?: string | null;
    details?: React.ReactNode;
    className?: string;
    avatarClassName?: string;
    nameClassName?: string;
    /**
     * Opt in to the coarse-pointer hit floor on BOTH anchors (`DISCOVERY-68`). Off by default, so the
     * ~37 existing call sites render byte-identically.
     *
     * <p>Use it wherever this row sits on top of a competing full-surface link. On the groups directory
     * card the attribution renders two separate anchors — a `size-5` avatar and a name in a `leading-5`
     * line box, both ~20 CSS px, both `relative z-layer-raised-front` so they deliberately outrank the
     * card's `absolute inset-0` link — with an 8 px dead zone between them. They do not merely fail the
     * 44 px floor, they fail it while stacked on a DIFFERENT destination: a thumb aimed at the top of a
     * card lands on the group, on the creator's profile, or between them, and the first two are
     * different pages. Desktop has a pointer and a hover wash to disambiguate; the phone has neither.
     *
     * <p><b>Why the expander rather than one merged anchor.</b> The row's direction was a single anchor
     * over avatar-plus-name. That cannot be built without discarding the 2026-08-03 first-line alignment
     * the docstring above exists to protect: the avatar is a `shrink-0` flex item while the name flows
     * INLINE with `details`, and lifting the name out of that inline flow is precisely the `inline-flex`
     * shape that used to drop the whole subtitle to the next line and leave a lone `username ·` on the
     * first. `touch-hit-44` reaches the same outcome with zero layout change: two centred 44 px slugs on
     * controls ~28 px apart, which overlap and therefore COVER the dead zone instead of leaving it.
     * `03-tap-targets.css`'s pitch rule warns about overlap because the later-painted sibling wins the
     * hit test — here both anchors carry the SAME `href`, so whichever wins, the member gets the
     * destination they aimed at. That is what makes this one target in practice while staying two in the
     * DOM, and it is why the flag is opt-in: the reasoning holds only where both anchors share an href
     * and nothing small is within 44 px of either.
     */
    coarsePointerHitFloor?: boolean;
    /**
     * `false` renders the name and details without the row's own avatar. For a host that draws the
     * avatar ELSEWHERE — the feed row's 40px gutter (SV-135 S2, design §5.2), where a second, inline
     * avatar would put the same face on the row twice. Defaults to `true`, so every other call site
     * renders byte-identically. The row keeps `data-author-attribution` and both class constants.
     */
    showAvatar?: boolean;
    /**
     * Rendered right after the name, inside the same inline text flow (the post header's distinction
     * icon, distinctions design §5). Absent at every other call site, which then renders byte-identically.
     */
    nameAdornment?: React.ReactNode;
    /**
     * Wraps the rendered name in a sentence (`PROFILE-27`, S7 C35: "On a post by {0}"). The host owns the
     * localized frame; this component only hands over the name node so the link, `translate="no"` and
     * the first-line alignment are untouched. Absent everywhere else, which renders byte-identically.
     */
    frameName?: (nameNode: React.ReactNode) => React.ReactNode;
    dataPlaywrightId?: string;
}) {
    const {
        name,
        localizer,
        href,
        imageUrl,
        avatarSeed,
        details,
        className,
        avatarClassName,
        nameClassName,
        coarsePointerHitFloor = false,
        showAvatar = true,
        nameAdornment,
        frameName,
        dataPlaywrightId,
    } = props;

    const resolvedAvatarSeed = React.useMemo(
        () => avatarSeed?.trim() || name?.trim() || getAvatarFallback(name),
        [avatarSeed, name],
    );

    const avatarNode = (
        <ProfileImage localizer={localizer}
            imageUrl={imageUrl}
            username={resolvedAvatarSeed}
            mode="user"
            hoverable={Boolean(href)}
            className={cn("size-5", avatarClassName)}
        />
    );

    const nameNode = href ? (
        <a
            href={href}
            // A display name is a PROPER NOUN. Without this, the reader's browser translator rewrites
            // it as a common noun — the member `Dei` reached Spanish readers as `Dios` (2026-09-03).
            // The page around it stays fully translatable; only the name is exempted. Design:
            // designs/01-now/2026-09-03-member-display-names-excluded-from-browser-translation-design.md
            translate="no"
            className={cn(
                "relative z-layer-raised-front wrap-anywhere font-medium text-text-main hover:underline",
                // `inline-block` because the utility centres a pseudo-element on the originating
                // element's box, and an inline anchor spanning a line break has no single box to centre
                // on. `relative` is repeated here even though the base string above already has it:
                // `tapTargets.contract.test.tsx` asserts per LINE, on purpose, because co-location in
                // one string is the only thing a text sweep can prove lands on the same element.
                // twMerge collapses the duplicate, so this costs nothing and keeps the guard strong.
                coarsePointerHitFloor && "relative inline-block touch-hit-44",
                nameClassName,
            )}
        >
            {name}
        </a>
    ) : (
        // Same reason as the anchor above: the unlinked branch is a different element, so it is a
        // second place to forget.
        <span translate="no" className={cn("wrap-anywhere font-medium text-text-main", nameClassName)}>{name}</span>
    );

    return (
        <div
            className={cn(AUTHOR_ATTRIBUTION_ROW_CLASS, "text-muted-foreground", className)}
            // A stable, ALWAYS-PRESENT hook for the first-line alignment invariant
            // (tests/card-author-attribution-alignment.spec.mjs). `dataPlaywrightId` cannot serve:
            // it is optional and, as of 2026-08-03, not one of the ~37 call sites passes it, so a
            // browser test had no way to address the row it is guarding. Additive — it changes no
            // existing selector.
            data-author-attribution=""
            data-playwright-id={dataPlaywrightId}
        >
            {!showAvatar ? null : href ? (
                <a
                    href={href}
                    className={cn(
                        "relative z-layer-raised-front shrink-0",
                        coarsePointerHitFloor && "relative touch-hit-44",
                    )}
                    // The accessible name IS the member's name, and translation engines rewrite
                    // `aria-label` too. Marking the element is the only signal available for it.
                    translate="no"
                    aria-label={name}
                >
                    {avatarNode}
                </a>
            ) : (
                <span className="shrink-0">{avatarNode}</span>
            )}

            <span className={cn(AUTHOR_ATTRIBUTION_TEXT_CLASS, "text-muted-foreground")}>
                {frameName ? frameName(nameNode) : nameNode}
                {nameAdornment ? <>{" "}{nameAdornment}</> : null}
                {details ? (
                    <>
                        {" "}
                        <span aria-hidden className="text-muted-foreground/70">·</span>{" "}
                        <span>{details}</span>
                    </>
                ) : null}
            </span>
        </div>
    );
}

export default AuthorAttribution;
